/**
 * JavaScript Format Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type PrettierModule = {
  format: (source: string, options: Record<string, unknown>) => string;
};

function unwrapDefault<T>(mod: unknown): T {
  const m = mod as Record<string, unknown> | null | undefined;
  return (m?.default ?? mod) as T;
}

export const JsFormatTool: ITransformerTool = {
  id: 'js-format',
  name: 'JavaScript Format',
  description: 'Format JavaScript using a standard style',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['javascript', 'js', 'format', 'beautify', 'prettify'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'printWidth',
      type: 'number',
      label: 'Print Width',
      defaultValue: 100,
      description: 'Preferred line length before wrapping',
      validation: { min: 40, max: 300, step: 10 },
    },
    {
      name: 'semi',
      type: 'boolean',
      label: 'Semicolons',
      defaultValue: true,
      description: 'Add semicolons at statement ends',
    },
    {
      name: 'singleQuote',
      type: 'boolean',
      label: 'Single Quotes',
      defaultValue: false,
      description: 'Prefer single quotes for strings',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const printWidth = typeof config.printWidth === 'number' ? (config.printWidth as number) : 100;
    const semi = config.semi !== false;
    const singleQuote = config.singleQuote === true;

    try {
      const prettierMod = await import('prettier/standalone');
      const babelMod = await import('prettier/plugins/babel');
      const estreeMod = await import('prettier/plugins/estree');

      const prettier = unwrapDefault<PrettierModule>(prettierMod);
      const babel = unwrapDefault<unknown>(babelMod); // Dynamic import interop
      const estree = unwrapDefault<unknown>(estreeMod); // Dynamic import interop

      return prettier.format(String(input ?? ''), {
        parser: 'babel',
        plugins: [babel, estree],
        printWidth,
        semi,
        singleQuote,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format JavaScript';
      throw ToolError.execution(message);
    }
  },
};
